import { useCallback } from "react";
import { useRouter } from "solito/router";
import { AlertCircle as AlertCircleIcon, ArrowRight as ArrowRightIcon } from "@tamagui/lucide-icons";
import { AlertBar, Avatar, useMedia } from "@medbillai/ui";
import { issueUrl } from "@medbillai/utils";
import { analyticsService } from "../../../lib/analytics";
import { useViewAllCasesAction } from "../../../provider/cases-panel";
import { attentionActionLabel } from "./attentionActionLabel";
import { MAX_HOME_CASES, type YourCase } from "./useYourCases";
const SOURCE = "attention-alert";
export type AttentionAlertProps = {
  blockedCases: YourCase[];
  /** Scrolls the page to the blocked band in "Your cases" (multiple, within cap). */
  onScrollToBlocked: () => void;
};
export function AttentionAlert({
  blockedCases,
  onScrollToBlocked
}: AttentionAlertProps) {
  const {
    push
  } = useRouter();
  const media = useMedia();
  const viewAllCases = useViewAllCasesAction(SOURCE);
  const blockedCount = blockedCases.length;
  const singleCase = blockedCount === 1 ? blockedCases[0] : undefined;
  const handlePress = useCallback(() => {
    analyticsService.trackEvent("AttentionAlertTapped", {
      source: SOURCE,
      blockedCount
    });

    // Single: open that case (fire OpenedIssue so it's joinable on id, like the
    // case rows). Within the list cap: scroll to the blocked band. Beyond it:
    // "view all" — the panel on desktop, else the full cases list.
    if (singleCase) {
      analyticsService.trackEvent("OpenedIssue", {
        id: singleCase.id,
        source: SOURCE
      });
      push(issueUrl({
        issueId: singleCase.id,
        title: singleCase.title
      }));
      return;
    }
    if (blockedCount > MAX_HOME_CASES) {
      viewAllCases.onPress();
      if (viewAllCases.href) {
        push(viewAllCases.href);
      }
      return;
    }
    onScrollToBlocked();
  }, [singleCase, blockedCount, push, onScrollToBlocked, viewAllCases]);

  // Omit-empty: nothing is blocked on the user.
  if (blockedCount === 0) {
    return null;
  }
  const title = singleCase ? attentionActionLabel(singleCase) : `${blockedCount} cases need your attention`;
  const subtitle = singleCase ? singleCase.title ?? "Tap to continue" : "Review your cases to unblock them";
  return <AlertBar testID="home.attention-alert" status="warning" bordered={false} prominent leading={<Avatar size={media.gtXs ? "medium" : "small"} shape="square" status="warning" backgroundColor="$bgWarningHover" icon={AlertCircleIcon} />} title={title} message={subtitle} trailingIcon={ArrowRightIcon} onPress={handlePress} data-sentry-element="AlertBar" data-sentry-component="AttentionAlert" data-sentry-source-file="AttentionAlert.tsx" />;
}